import { DefaultRootState } from 'react-redux';

import { Nullable } from 'types';
import {
    changeAllBidsToRecommended,
    changeAllBudgetsToRecommended,
    changeAllFields,
    changeBidFrequencyStatus,
    deleteMultiCreationItem,
    sortVacanciesBySelectedAndAdded,
} from 'utils/multiCreation';

import createReducer, { ActionCreatorHelper } from 'store/createReducer';
import { AuctionCampaign, AuctionCampaignStatus, BiddingStrategy, BidFrequencyStatus } from 'store/models/auctionData';

export enum AuctionCreateFieldKey {
    Bid = 'bid',
    Budget = 'budget',
    StartAt = 'startAt',
    EndAt = 'endAt',
}

export const enum VacancyFilterKeys {
    Areas = 'areas',
    Users = 'users',
}

export const enum AuctionCampaignCreationStatus {
    Disabled = 'DISABLED',
    NotCreated = 'NOT_CREATED',
    CampaignDraftCreated = 'CAMPAIGN_DRAFT_CREATED',
    CampaignCreated = 'CAMPAIGN_CREATED',
}

export interface Vacancy {
    vacancyId: number;
    name: string;
    url: string;
    areaName: string;
    foreignArea: boolean;
    managerName: string;
    auctionCampaignCreationStatus: AuctionCampaignCreationStatus;
    campaignAuctionStatus: AuctionCampaignStatus | null;
}

export interface FilterOption {
    value: string;
    text: string;
}

export type MultiCreationFilter = { [key in VacancyFilterKeys]: FilterOption[] };

const EMPTY_FILTERS: MultiCreationFilter = { [VacancyFilterKeys.Areas]: [], [VacancyFilterKeys.Users]: [] };

export interface VacanciesAuctionInfoItem {
    currency: string;
    bid: {
        disabled: boolean;
        minValue: number;
        value: number;
    };
    budget: {
        disabled: boolean;
        minValue: number;
        value: number;
    };
    bidRating: {
        competingCampaignBids: number[];
        competingCampaignIds: number[];
        bidFrequencyStatus: BidFrequencyStatus;
        recommendedBudget: number;
        oftenThresholdBid: number;
    };
    auctionCampaign: Nullable<AuctionCampaign>;
    bidRatingParams: {
        name: string;
        text: string;
        areaIds: number[];
        professionalRoleIds: number[];
        vacancyId: number;
    };
    vacancy: {
        vacancyId: number;
        name: string;
        url: string;
        areaName: string;
    };
    campaignCreationStatus: AuctionCampaignCreationStatus;
}

export interface AuctionsToCreateItem {
    bid: string;
    minBidValue: number;
    bidFrequencyStatus: BidFrequencyStatus | null;
    bidValidationError: string | null;
    budget: string;
    budgetValidationError: string | null;
    vacancyId: number;
    isCreationFailed: boolean;
    startAt: string | null;
    startAtValidationError: string | null;
    endAt: string | null;
    endAtValidationError: string | null;
    biddingStrategy?: BiddingStrategy;
}

export type VacanciesAuctionInfo = Record<number, VacanciesAuctionInfoItem>;
export type AuctionsToCreate = Record<number, AuctionsToCreateItem>;

export interface Audience {
    audiencePerWeek: number;
    audienceCapacityPerWeek: number;
    audienceFillRate: number;
}

export interface VacancyBids {
    vacancyId: number;
    bidInCents: number;
}

export interface VacancyBidsRatings {
    vacancyId: number;
    bidFrequencyStatus: BidFrequencyStatus | null;
}

export enum MultiCreationAuctionSteps {
    VacanciesSelectStep = 'vacanciesSelectStep',
    BiddingStrategySelectStep = 'biddingStrategySelectStep',
    BidsAndBudgetsStep = 'bidsAndBudgetsStep',
}

// ----------------------- actions -------------------------------------
const APPLY_MULTI_CREATION_FILTERS = 'APPLY_MULTI_CREATION_FILTERS';
const SET_MULTI_CREATION_FILTERS = 'SET_MULTI_CREATION_FILTERS';
const SET_MULTI_CREATION_VACANCIES_SEARCH_VALUE = 'SET_MULTI_CREATION_VACANCIES_SEARCH_VALUE';
const SET_MULTI_CREATION_SELECTED_FILTERS_BY_FILTER_KEY = 'SET_MULTI_CREATION_SELECTED_FILTERS_BY_FILTER_KEY';
const RESET_MULTI_CREATION_FILTERS = 'RESET_MULTI_CREATION_FILTERS';
const RESET_MULTI_CREATION_SELECTED_FILTERS_TO_APPLIED = 'RESET_MULTI_CREATION_SELECTED_FILTERS_TO_APPLIED';

const SET_MULTI_CREATION_SELECTED_VACANCY_IDS = 'SET_MULTI_CREATION_SELECTED_VACANCY_IDS';
const SET_MULTI_CREATION_VACANCIES = 'SET_MULTI_CREATION_VACANCIES';
const SET_NOT_AVAILABLE_FOR_CREATION_VACANCIES = 'SET_NOT_AVAILABLE_FOR_CREATION_VACANCIES';
const SET_MULTI_CREATION_VACANCIES_IS_LOADING = 'SET_MULTI_CREATION_VACANCIES_IS_LOADING';

const SET_MULTI_CREATION_ACTIVE_STEP = 'SET_MULTI_CREATION_ACTIVE_STEP';
const SET_MULTI_CREATION_BIDDING_STRATEGY = 'SET_MULTI_CREATION_BIDDING_STRATEGY';

const DELETE_MULTI_CREATION_VACANCIES_AUCTION_INFO_ITEM_BY_ID =
    'DELETE_MULTI_CREATION_VACANCIES_AUCTION_INFO_ITEM_BY_ID';
const SET_MULTI_CREATION_VACANCIES_AUCTION_INFO = 'SET_MULTI_CREATION_VACANCIES_AUCTION_INFO';
const SET_MULTI_CREATION_AUCTION_TO_INFO_BID = 'SET_MULTI_CREATION_AUCTION_TO_INFO_BID';
const SET_MULTI_CREATION_BID_FREQUENCY_STATUS = 'SET_MULTI_CREATION_BID_FREQUENCY_STATUS';
const SET_MULTI_CREATION_BID_VALIDATION_ERROR = 'SET_MULTI_CREATION_BID_VALIDATION_ERROR';
const SET_MULTI_CREATION_BUDGET = 'SET_MULTI_CREATION_BUDGET';
const SET_MULTI_CREATION_BUDGET_VALIDATION_ERROR = 'SET_MULTI_CREATION_BUDGET_VALIDATION_ERROR';
const SET_MULTI_CREATION_BIDS_TO_RECOMMENDED = 'SET_MULTI_CREATION_BIDS_TO_RECOMMENDED';
const SET_MULTI_CREATION_BUDGETS_TO_RECOMMENDED = 'SET_MULTI_CREATION_BUDGETS_TO_RECOMMENDED';
const SET_MULTI_CREATION_START_AT = 'SET_MULTI_CREATION_START_AT';
const SET_MULTI_CREATION_START_AT_VALIDATION_ERROR = 'SET_MULTI_CREATION_START_AT_VALIDATION_ERROR';
const SET_MULTI_CREATION_END_AT = 'SET_MULTI_CREATION_END_AT';
const SET_MULTI_CREATION_END_AT_VALIDATION_ERROR = 'SET_MULTI_CREATION_END_AT_VALIDATION_ERROR';
const UPDATE_MULTI_CREATION_FIELD = 'UPDATE_MULTI_CREATION_FIELD';
const UPDATE_MULTI_CREATION_AUCTIONS_BID_FREQUENCY_STATUS = 'UPDATE_MULTI_CREATION_AUCTIONS_BID_FREQUENCY_STATUS';
const SET_MULTI_CREATION_BID_FREQUENCY_STATUS_LOADING = 'SET_MULTI_CREATION_BID_FREQUENCY_STATUS_LOADING';
const SET_MULTI_CREATION_CAMPAIGNS_CREATING = 'SET_MULTI_CREATION_CAMPAIGNS_CREATING';

interface PayloadTypes {
    [APPLY_MULTI_CREATION_FILTERS]: null;
    [SET_MULTI_CREATION_FILTERS]: MultiCreationFilter;
    [SET_MULTI_CREATION_SELECTED_FILTERS_BY_FILTER_KEY]: { filterKey: VacancyFilterKeys; newValues?: FilterOption[] };
    [SET_MULTI_CREATION_VACANCIES_SEARCH_VALUE]: string;
    [RESET_MULTI_CREATION_FILTERS]: null;
    [RESET_MULTI_CREATION_SELECTED_FILTERS_TO_APPLIED]: null;

    [SET_MULTI_CREATION_SELECTED_VACANCY_IDS]: Record<number, boolean>;
    [SET_MULTI_CREATION_VACANCIES]: Vacancy[];
    [SET_NOT_AVAILABLE_FOR_CREATION_VACANCIES]: Vacancy[];
    [SET_MULTI_CREATION_VACANCIES_IS_LOADING]: boolean;

    [SET_MULTI_CREATION_ACTIVE_STEP]: MultiCreationAuctionSteps;
    [SET_MULTI_CREATION_BIDDING_STRATEGY]: BiddingStrategy;

    [DELETE_MULTI_CREATION_VACANCIES_AUCTION_INFO_ITEM_BY_ID]: number;
    [SET_MULTI_CREATION_VACANCIES_AUCTION_INFO]: {
        vacanciesAuctionInfo: VacanciesAuctionInfo | null;
        auctionsToCreate: AuctionsToCreate;
    };
    [SET_MULTI_CREATION_AUCTION_TO_INFO_BID]: { vacancyId: number; value: string };
    [SET_MULTI_CREATION_BID_FREQUENCY_STATUS]: {
        vacancyId: number;
        bidFrequencyStatus: BidFrequencyStatus;
    };
    [SET_MULTI_CREATION_BID_VALIDATION_ERROR]: {
        vacancyId: number;
        error: string | null;
    };
    [SET_MULTI_CREATION_BUDGET]: { vacancyId: number; value: string };
    [SET_MULTI_CREATION_BUDGET_VALIDATION_ERROR]: {
        vacancyId: number;
        error: string | null;
    };
    [SET_MULTI_CREATION_BIDS_TO_RECOMMENDED]: void;
    [SET_MULTI_CREATION_BUDGETS_TO_RECOMMENDED]: void;
    [SET_MULTI_CREATION_START_AT]: { vacancyId: number; value: string | null };
    [SET_MULTI_CREATION_START_AT_VALIDATION_ERROR]: {
        vacancyId: number;
        error: string | null;
    };
    [SET_MULTI_CREATION_END_AT]: { vacancyId: number; value: string | null };
    [SET_MULTI_CREATION_END_AT_VALIDATION_ERROR]: {
        vacancyId: number;
        error: string | null;
    };
    [UPDATE_MULTI_CREATION_FIELD]: { fieldKey: AuctionCreateFieldKey; newValue: string | null };
    [UPDATE_MULTI_CREATION_AUCTIONS_BID_FREQUENCY_STATUS]: VacancyBidsRatings[];
    [SET_MULTI_CREATION_BID_FREQUENCY_STATUS_LOADING]: boolean;
    [SET_MULTI_CREATION_CAMPAIGNS_CREATING]: boolean;
}

const ActionCreator = ActionCreatorHelper<PayloadTypes>();

export const applyMultiCreationFilters = ActionCreator(APPLY_MULTI_CREATION_FILTERS);
export const setMultiCreationFilters = ActionCreator(SET_MULTI_CREATION_FILTERS);
export const setMultiCreationVacanciesSearchValue = ActionCreator(SET_MULTI_CREATION_VACANCIES_SEARCH_VALUE);
export const setMultiCreationSelectedFiltersByFilterKey = ActionCreator(
    SET_MULTI_CREATION_SELECTED_FILTERS_BY_FILTER_KEY
);
export const resetMultiCreationFilters = ActionCreator(RESET_MULTI_CREATION_FILTERS);
export const resetMultiCreationSelectedFiltersToApplied = ActionCreator(
    RESET_MULTI_CREATION_SELECTED_FILTERS_TO_APPLIED
);

export const setMultiCreationSelectedVacancyIds = ActionCreator(SET_MULTI_CREATION_SELECTED_VACANCY_IDS);
export const setMultiCreationVacancies = ActionCreator(SET_MULTI_CREATION_VACANCIES);
export const setNotAvailableForCreationVacancies = ActionCreator(SET_NOT_AVAILABLE_FOR_CREATION_VACANCIES);
export const setMultiCreationVacanciesIsLoading = ActionCreator(SET_MULTI_CREATION_VACANCIES_IS_LOADING);

export const setMultiCreationActiveStep = ActionCreator(SET_MULTI_CREATION_ACTIVE_STEP);
export const setMultiCreationBiddingStrategy = ActionCreator(SET_MULTI_CREATION_BIDDING_STRATEGY);

export const deleteMultiCreationVacanciesAuctionInfoItemById = ActionCreator(
    DELETE_MULTI_CREATION_VACANCIES_AUCTION_INFO_ITEM_BY_ID
);
export const setMultiCreationVacanciesAuctionInfo = ActionCreator(SET_MULTI_CREATION_VACANCIES_AUCTION_INFO);
export const setMultiCreationBid = ActionCreator(SET_MULTI_CREATION_AUCTION_TO_INFO_BID);
export const setMultiCreationBidFrequencyStatus = ActionCreator(SET_MULTI_CREATION_BID_FREQUENCY_STATUS);
export const setMultiCreationBidValidationError = ActionCreator(SET_MULTI_CREATION_BID_VALIDATION_ERROR);
export const setMultiCreationBudget = ActionCreator(SET_MULTI_CREATION_BUDGET);
export const setMultiCreationBudgetValidationError = ActionCreator(SET_MULTI_CREATION_BUDGET_VALIDATION_ERROR);
export const setMultiCreationBidsToRecommended = ActionCreator(SET_MULTI_CREATION_BIDS_TO_RECOMMENDED);
export const setMultiCreationBudgetsToRecommended = ActionCreator(SET_MULTI_CREATION_BUDGETS_TO_RECOMMENDED);
export const setMultiCreationStartAt = ActionCreator(SET_MULTI_CREATION_START_AT);
export const setMultiCreationStartAtValidationError = ActionCreator(SET_MULTI_CREATION_START_AT_VALIDATION_ERROR);
export const setMultiCreationEndAt = ActionCreator(SET_MULTI_CREATION_END_AT);
export const setMultiCreationEndAtValidationError = ActionCreator(SET_MULTI_CREATION_END_AT_VALIDATION_ERROR);
export const updateMultiCreationField = ActionCreator(UPDATE_MULTI_CREATION_FIELD);
export const updateMultiCreationAuctionsBidFrequencyStatus = ActionCreator(
    UPDATE_MULTI_CREATION_AUCTIONS_BID_FREQUENCY_STATUS
);
export const setMultiCreationBidFrequencyStatusLoading = ActionCreator(SET_MULTI_CREATION_BID_FREQUENCY_STATUS_LOADING);
export const setMultiCreationCampaignsCreating = ActionCreator(SET_MULTI_CREATION_CAMPAIGNS_CREATING);
// ----------------------- /actions -------------------------------------

// ----------------------- selectors ------------------------------------

export const selectMultiCreationVacanciesAuctionInfo = (state: DefaultRootState): VacanciesAuctionInfo =>
    state.multiCreation.vacanciesAuctionInfo as VacanciesAuctionInfo;

export const selectMultiCreationBidFrequencyStatusLoading = (state: DefaultRootState): boolean =>
    state.multiCreation.bidFrequencyStatusLoading;

export const selectMultiCreationCampaignsCreating = (state: DefaultRootState): boolean =>
    state.multiCreation.isCampaignsCreating;

// ----------------------- /selectors -----------------------------------

// ----------------------- reducer --------------------------------------
interface MultiCreationState {
    appliedFilters: MultiCreationFilter;
    auctionsToCreate: AuctionsToCreate;
    filters: MultiCreationFilter;
    selectedFilters: MultiCreationFilter;
    selectedVacancyIds: Record<number, boolean>;
    vacancies: Vacancy[];
    notAvailableForCreationVacancies: Vacancy[];
    vacanciesAuctionInfo: VacanciesAuctionInfo | null;
    vacanciesIsLoading: boolean;
    vacancySearchValue: string;
    bidFrequencyStatusLoading: boolean;
    isCampaignsCreating: boolean;
    biddingStrategy: BiddingStrategy;
    activeStep: MultiCreationAuctionSteps;
}

export const MULTI_CREATION_INITIAL_STATE: MultiCreationState = {
    appliedFilters: EMPTY_FILTERS,
    auctionsToCreate: {},
    filters: EMPTY_FILTERS,
    selectedFilters: EMPTY_FILTERS,
    selectedVacancyIds: {},
    vacancies: [],
    notAvailableForCreationVacancies: [],
    vacanciesAuctionInfo: null,
    vacanciesIsLoading: false,
    vacancySearchValue: '',
    bidFrequencyStatusLoading: false,
    isCampaignsCreating: false,
    biddingStrategy: BiddingStrategy.Auto,
    activeStep: MultiCreationAuctionSteps.VacanciesSelectStep,
};

export default createReducer<MultiCreationState, PayloadTypes>(MULTI_CREATION_INITIAL_STATE, {
    [SET_MULTI_CREATION_VACANCIES]: (state, { payload }) => ({
        ...state,
        vacancies: sortVacanciesBySelectedAndAdded(payload, state.selectedVacancyIds, state.vacanciesAuctionInfo),
    }),
    [SET_NOT_AVAILABLE_FOR_CREATION_VACANCIES]: (state, { payload }) => ({
        ...state,
        notAvailableForCreationVacancies: payload,
    }),
    [SET_MULTI_CREATION_FILTERS]: (state, { payload }) => ({
        ...state,
        filters: payload,
    }),
    [SET_MULTI_CREATION_SELECTED_FILTERS_BY_FILTER_KEY]: (state, { payload }) => ({
        ...state,
        selectedFilters: { ...state.selectedFilters, [payload.filterKey]: payload.newValues ?? [] },
    }),
    [APPLY_MULTI_CREATION_FILTERS]: (state) => ({
        ...state,
        appliedFilters: { ...state.selectedFilters },
    }),

    [RESET_MULTI_CREATION_SELECTED_FILTERS_TO_APPLIED]: (state) => ({
        ...state,
        selectedFilters: { ...state.appliedFilters },
    }),
    [RESET_MULTI_CREATION_FILTERS]: (state) => ({
        ...state,
        selectedFilters: { ...EMPTY_FILTERS },
        appliedFilters: { ...EMPTY_FILTERS },
    }),

    [SET_MULTI_CREATION_SELECTED_VACANCY_IDS]: (state, { payload }) => ({
        ...state,
        selectedVacancyIds: payload,
        vacancies: sortVacanciesBySelectedAndAdded(state.vacancies, payload, state.vacanciesAuctionInfo),
    }),
    [SET_MULTI_CREATION_VACANCIES_SEARCH_VALUE]: (state, { payload }) => ({
        ...state,
        vacancySearchValue: payload,
    }),
    [SET_MULTI_CREATION_VACANCIES_IS_LOADING]: (state, { payload }) => ({
        ...state,
        vacanciesIsLoading: payload,
    }),

    [SET_MULTI_CREATION_ACTIVE_STEP]: (state, { payload }) => ({
        ...state,
        activeStep: payload,
    }),
    [SET_MULTI_CREATION_BIDDING_STRATEGY]: (state, { payload }) => ({
        ...state,
        biddingStrategy: payload,
    }),

    [DELETE_MULTI_CREATION_VACANCIES_AUCTION_INFO_ITEM_BY_ID]: (state, { payload }) => ({
        ...state,
        ...deleteMultiCreationItem(state.auctionsToCreate, state.vacanciesAuctionInfo, payload),
    }),
    [SET_MULTI_CREATION_VACANCIES_AUCTION_INFO]: (state, { payload }) => ({
        ...state,
        vacanciesAuctionInfo: payload.vacanciesAuctionInfo,
        auctionsToCreate: payload.auctionsToCreate,
    }),
    [SET_MULTI_CREATION_AUCTION_TO_INFO_BID]: (state, { payload }) => ({
        ...state,
        auctionsToCreate: {
            ...state.auctionsToCreate,
            [payload.vacancyId]: {
                ...state.auctionsToCreate[payload.vacancyId],
                bid: payload.value,
            },
        },
    }),
    [SET_MULTI_CREATION_BID_FREQUENCY_STATUS]: (state, { payload }) => ({
        ...state,
        auctionsToCreate: {
            ...state.auctionsToCreate,
            [payload.vacancyId]: {
                ...state.auctionsToCreate[payload.vacancyId],
                bidFrequencyStatus: payload.bidFrequencyStatus,
            },
        },
    }),
    [SET_MULTI_CREATION_BID_VALIDATION_ERROR]: (state, { payload }) => ({
        ...state,
        auctionsToCreate: {
            ...state.auctionsToCreate,
            [payload.vacancyId]: {
                ...state.auctionsToCreate[payload.vacancyId],
                bidValidationError: payload.error,
            },
        },
    }),
    [SET_MULTI_CREATION_BUDGET]: (state, { payload }) => ({
        ...state,
        auctionsToCreate: {
            ...state.auctionsToCreate,
            [payload.vacancyId]: {
                ...state.auctionsToCreate[payload.vacancyId],
                budget: payload.value,
            },
        },
    }),
    [SET_MULTI_CREATION_BUDGET_VALIDATION_ERROR]: (state, { payload }) => ({
        ...state,
        auctionsToCreate: {
            ...state.auctionsToCreate,
            [payload.vacancyId]: {
                ...state.auctionsToCreate[payload.vacancyId],
                budgetValidationError: payload.error,
            },
        },
    }),
    [SET_MULTI_CREATION_BIDS_TO_RECOMMENDED]: (state) => ({
        ...state,
        auctionsToCreate: changeAllBidsToRecommended(
            state.auctionsToCreate,
            state.vacanciesAuctionInfo,
            state.biddingStrategy
        ),
    }),
    [SET_MULTI_CREATION_BUDGETS_TO_RECOMMENDED]: (state) => ({
        ...state,
        auctionsToCreate: changeAllBudgetsToRecommended(state.auctionsToCreate, state.vacanciesAuctionInfo),
    }),
    [SET_MULTI_CREATION_START_AT]: (state, { payload }) => ({
        ...state,
        auctionsToCreate: {
            ...state.auctionsToCreate,
            [payload.vacancyId]: {
                ...state.auctionsToCreate[payload.vacancyId],
                startAt: payload.value,
            },
        },
    }),
    [SET_MULTI_CREATION_START_AT_VALIDATION_ERROR]: (state, { payload }) => ({
        ...state,
        auctionsToCreate: {
            ...state.auctionsToCreate,
            [payload.vacancyId]: {
                ...state.auctionsToCreate[payload.vacancyId],
                startAtValidationError: payload.error,
            },
        },
    }),
    [SET_MULTI_CREATION_END_AT]: (state, { payload }) => ({
        ...state,
        auctionsToCreate: {
            ...state.auctionsToCreate,
            [payload.vacancyId]: {
                ...state.auctionsToCreate[payload.vacancyId],
                endAt: payload.value,
            },
        },
    }),
    [SET_MULTI_CREATION_END_AT_VALIDATION_ERROR]: (state, { payload }) => ({
        ...state,
        auctionsToCreate: {
            ...state.auctionsToCreate,
            [payload.vacancyId]: {
                ...state.auctionsToCreate[payload.vacancyId],
                endAtValidationError: payload.error,
            },
        },
    }),
    [UPDATE_MULTI_CREATION_FIELD]: (state, { payload }) => ({
        ...state,
        auctionsToCreate: changeAllFields(state.auctionsToCreate, payload.fieldKey, payload.newValue),
    }),
    [UPDATE_MULTI_CREATION_AUCTIONS_BID_FREQUENCY_STATUS]: (state, { payload }) => ({
        ...state,
        auctionsToCreate: changeBidFrequencyStatus(state.auctionsToCreate, payload),
    }),
    [SET_MULTI_CREATION_BID_FREQUENCY_STATUS_LOADING]: (state, { payload }) => ({
        ...state,
        bidFrequencyStatusLoading: payload,
    }),
    [SET_MULTI_CREATION_CAMPAIGNS_CREATING]: (state, { payload }) => ({
        ...state,
        isCampaignsCreating: payload,
    }),
});
// ----------------------- /reducer --------------------------------------
